/**
 * Phase 4 — canonical referral sources. Mirrors backend/src/config/referralSources.ts.
 *
 * The old option list was a plain string array copy-pasted into ManagerCreateUserView and
 * ManagerUserView, which is how "CDL Consultant" and "CDL Consultants" ended up as two sources.
 * Codes here, labels for display; the API accepts only the codes.
 */

export const REFERRAL_SOURCES = [
  'CDL_CONSULTANTS',
  'CDL_LEGAL',
  'TVC',
  'OTR',
  'LEGALEASE',
  'LEGAL_RESOURCES',
  'METLIFE',
  'LRIS',
  'AVVO',
  'SIMPLEX',
  'ARAG',
  'US_LEGAL',
  'TEXAS_LEGAL',
  'LEGAL_SHIELD',
  'REPEAT',
  'GOOGLE',
  'OTHER',
] as const;

export type ReferralSource = (typeof REFERRAL_SOURCES)[number];

export const REFERRAL_SOURCE_LABELS: Record<ReferralSource, string> = {
  CDL_CONSULTANTS: 'CDL Consultants',
  CDL_LEGAL: 'CDL Legal',
  TVC: 'TVC',
  OTR: 'Off the Record',
  LEGALEASE: 'LegalEase',
  LEGAL_RESOURCES: 'Legal Resources',
  METLIFE: 'MetLife',
  LRIS: 'LRIS',
  AVVO: 'AVVO',
  SIMPLEX: 'Simplex',
  ARAG: 'ARAG',
  US_LEGAL: 'US Legal',
  TEXAS_LEGAL: 'Texas Legal',
  LEGAL_SHIELD: 'Legal Shield/Riggs Abney',
  REPEAT: 'Repeat',
  GOOGLE: 'Google',
  OTHER: 'Other',
};

export function referralSourceLabel(code: string | null): string {
  if (!code) return 'Not set';
  return REFERRAL_SOURCE_LABELS[code as ReferralSource] ?? code;
}

/**
 * Options for the client's `User_Source` dropdown, which is legacy free text and stores the LABEL
 * rather than the code. `normalizeReferralSource` maps the labels back on read.
 *
 * `current` is included when it is not one of the labels, because a MUI Select whose value is not
 * among its options renders empty — and the existing rows hold drift like "CDL Consultant" singular.
 * Showing a staff member a blank Source on a client who has one would read as data loss.
 *
 * Both this screen and the create screen previously held their own hand-written copy of this list,
 * which is how LegalEase, Legal Resources and MetLife were absent from the dropdown while sitting in
 * the canonical list all along. Hannah reported all three.
 */
export function userSourceOptions(current?: string | null): string[] {
  const labels = REFERRAL_SOURCES.map((code) => REFERRAL_SOURCE_LABELS[code]);
  const trimmed = (current ?? '').trim();
  return trimmed && !labels.includes(trimmed) ? [...labels, trimmed] : labels;
}
